import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_ACTIVE_PROMOTION } from '@shared/graphql/queries/promotions';
import { GET_UPCOMING_RACES_TEASER, GET_PREVIEW_MEETINGS_OPTIMIZED } from '@shared/graphql/queries';
import { useSiteTagline } from '../contexts/SiteConfigContext';
import analyticsService from '../services/analytics';
import { UpcomingTips } from '../components';
import { MeetingTile } from '../components/home/MeetingTile';
import { getTodayAtNoon, formatDateForApi } from '../utils';

interface ActivePromotion {
  id: string;
  source: string;
  discountCode: string;
  title: string;
  description?: string;
  imageUrl?: string;
  imageFilename?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const PromoPage: React.FC = () => {
  const navigate = useNavigate();
  const siteTagline = useSiteTagline();
  const [copied, setCopied] = useState(false);
  
  // Use GraphQL query to fetch active promotion
  const { data, loading, error } = useQuery(GET_ACTIVE_PROMOTION, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  });

  const promotion = data?.getActivePromotion as ActivePromotion | null;

  // Query upcoming races teaser for logged out users
  const { loading: teaserLoading, error: teaserError, data: teaserData } = useQuery(GET_UPCOMING_RACES_TEASER, {
    variables: {
      limit: 2
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
    pollInterval: 60000, // Refresh every 60 seconds
  });

  // Get today's date for meeting data
  const todayDate = getTodayAtNoon();
  const formattedDate = formatDateForApi(todayDate);
  
  // Query today's meetings for logged-out users
  const { loading: meetingsLoading, error: meetingsError, data: meetingsData } = useQuery(GET_PREVIEW_MEETINGS_OPTIMIZED, {
    variables: {
      date: formattedDate,
      analysisTypeId: 1
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network'
  });

  // Group meetings by category and limit to top performers
  const groupMeetingsByCategory = (meetings: any[] = []) => {
    const grouped = {
      metropolitan: [] as any[],
      provincial: [] as any[],
      country: [] as any[]
    };
    
    // Helper function to get total winner percentage for sorting
    const getWinnerPercentage = (meeting: any): number => {
      if (!meeting.performanceImproved) return 0;
      const perf = meeting.performanceImproved;
      const totalWinners = perf.top_1_winners + perf.top_2_winners + perf.top_3_winners + perf.value_winners;
      return perf.total_races > 0 ? (totalWinners / perf.total_races) * 100 : 0;
    };
    
    // Group meetings by category
    meetings.forEach((meeting) => {
      const type = meeting.track.type.toLowerCase();
      if (type === 'metropolitan') {
        grouped.metropolitan.push(meeting);
      } else if (type === 'provincial') {
        grouped.provincial.push(meeting);
      } else {
        grouped.country.push(meeting);
      }
    });
    
    // Sort each category by winner percentage (highest first) and limit to top 3
    const sortByPerformance = (a: any, b: any) => getWinnerPercentage(b) - getWinnerPercentage(a);
    
    grouped.metropolitan.sort(sortByPerformance);
    grouped.provincial.sort(sortByPerformance);
    grouped.country.sort(sortByPerformance);
    
    // Limit to top 3 from each category for preview
    return {
      metropolitan: grouped.metropolitan.slice(0, 3),
      provincial: grouped.provincial.slice(0, 3),
      country: grouped.country.slice(0, 3)
    };
  };
  
  const groupedMeetings = groupMeetingsByCategory(meetingsData?.previewMeetingsOptimized);
  const hasAnyMeetings = (groupedMeetings.metropolitan.length + groupedMeetings.provincial.length + groupedMeetings.country.length) > 0;

  // Track page view when component mounts and promotion is loaded
  useEffect(() => {
    if (!loading && (promotion || error)) {
      try {
        analyticsService.trackPageView('promo', undefined, new Date().toISOString().split('T')[0], {
          promotionId: promotion?.id,
          promotionSource: promotion?.source,
          promotionTitle: promotion?.title,
          hasActivePromotion: !!promotion,
          loadError: !!error,
          referrer: document.referrer || 'direct'
        });
        console.log('📊 PromoPage: Page view tracked');
      } catch (analyticsError) {
        console.warn('Analytics tracking failed:', analyticsError);
      }
    }
  }, [loading, promotion, error]);

  // When user visits /promo page, set session storage to track they came from here
  useEffect(() => {
    if (promotion) {
      // Set promo source in session storage to track that user came from /promo page
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('promo_source', promotion.source);
        console.log('🎯 PromoPage: Set promo source in storage:', promotion.source);
      }
    }
  }, [promotion]);

  // Redirect to homepage if no active promotion (after loading is complete)
  useEffect(() => {
    if (!loading && !promotion && !error) {
      console.log('🎯 PromoPage: No active promotion, redirecting to homepage');
      navigate('/');
    }
  }, [loading, promotion, error, navigate]);

  const handleGetStarted = () => {
    if (!promotion) return;
    
    // Track the sign up button click
    try {
      analyticsService.trackEvent('promo_signup_click', {
        pageType: 'promo',
        selectedDate: new Date().toISOString().split('T')[0],
        metadata: {
          promotionId: promotion.id,
          promotionSource: promotion.source,
          discountCode: promotion.discountCode || 'no_discount_code',
          promotionTitle: promotion.title,
          buttonType: 'get_started'
        }
      });
      console.log('📊 PromoPage: Sign up button click tracked');
    } catch (analyticsError) {
      console.warn('Analytics tracking failed for sign up click:', analyticsError);
    }
    
    // Navigate to registration page with conditional discount code and campaign source
    const params = new URLSearchParams();
    if (promotion.discountCode) {
      params.append('discount', promotion.discountCode);
    }
    params.append('source', promotion.source);
    navigate(`/register?${params.toString()}`);
  };

  const handleSignIn = () => {
    if (!promotion) return;
    
    // Track the sign in button click
    try {
      analyticsService.trackEvent('promo_signin_click', {
        pageType: 'promo',
        selectedDate: new Date().toISOString().split('T')[0],
        metadata: {
          promotionId: promotion.id,
          promotionSource: promotion.source,
          discountCode: promotion.discountCode || 'no_discount_code',
          promotionTitle: promotion.title,
          buttonType: 'sign_in'
        }
      });
      console.log('📊 PromoPage: Sign in button click tracked');
    } catch (analyticsError) {
      console.warn('Analytics tracking failed for sign in click:', analyticsError);
    }
    
    navigate(`/login?source=${promotion.source}`);
  };

  const handleCopyCode = async () => {
    if (!promotion || !promotion.discountCode) return;
    
    // Track the discount code copy action
    try {
      analyticsService.trackEvent('promo_discount_code_copy', {
        pageType: 'promo',
        selectedDate: new Date().toISOString().split('T')[0],
        metadata: {
          promotionId: promotion.id,
          promotionSource: promotion.source,
          discountCode: promotion.discountCode,
          promotionTitle: promotion.title,
          actionType: 'copy_discount_code'
        }
      });
      console.log('📊 PromoPage: Discount code copy tracked');
    } catch (analyticsError) {
      console.warn('Analytics tracking failed for discount code copy:', analyticsError);
    }
    
    try {
      await navigator.clipboard.writeText(promotion.discountCode);
      setCopied(true);
      
      // Reset the "copied" state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy discount code:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = promotion.discountCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-racing-gray">
        {/* Header with Logo */}
        <header className="site-header">
          <div className="flex justify-between items-center w-full">
            <Link to="/" className="flex flex-col items-start md:flex-row md:items-center">
              <div className="flex items-center">
                <img
                  src="/tippingiq_transparent.svg"
                  alt="TippingIQ"
                  className="h-10 md:h-10 w-auto"
                />
              </div>
              <span className="mobile-tagline md:ml-4">
                {siteTagline}
              </span>
            </Link>
          </div>
        </header>

        {/* Empty Date Navigation Bar */}
        <div className="date-navigation">
          <div className="flex flex-col sm:flex-row justify-center items-center w-full px-4">
            {/* Empty space - no buttons */}
          </div>
        </div>
  
        <main className="flex-grow">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-racing-green mx-auto mb-4"></div>
              <p className="text-gray-600">Loading exclusive offer...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state or no promotion
  if (error || !promotion) {
    return (
      <div className="min-h-screen flex flex-col bg-racing-gray">
        {/* Header with Logo */}
        <header className="site-header">
          <div className="flex justify-between items-center w-full">
            <Link to="/" className="flex flex-col items-start md:flex-row md:items-center">
              <div className="flex items-center">
                <img
                  src="/tippingiq_transparent.svg"
                  alt="TippingIQ"
                  className="h-10 md:h-10 w-auto"
                />
              </div>
              <span className="mobile-tagline md:ml-4">
                {siteTagline}
              </span>
            </Link>
          </div>
        </header>

        {/* Empty Date Navigation Bar */}
        <div className="date-navigation">
          <div className="flex flex-col sm:flex-row justify-center items-center w-full px-4">
            {/* Empty space - no buttons */}
          </div>
        </div>

        <main className="flex-grow">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <span className="text-2xl">❌</span>
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">No Active Promotion</h2>
              <p className="text-gray-600 mb-6">
                {error ? 'Unable to load promotion at this time.' : 'There are no active promotions at this time.'}
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-racing-green hover:bg-racing-green-dark text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
              >
                View Regular Pricing
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Active promotion display
  return (
    <div className="min-h-screen flex flex-col bg-racing-gray">
      {/* Header with Logo */}
      <header className="site-header">
        <div className="flex justify-between items-center w-full">
          <Link to="/" className="flex flex-col items-start md:flex-row md:items-center">
            <div className="flex items-center">
              <img
                src="/tippingiq_transparent.svg"
                alt="TippingIQ"
                className="h-10 md:h-10 w-auto"
              />
            </div>
            <span className="mobile-tagline md:ml-4">
              {siteTagline}
            </span>
          </Link>
        </div>
      </header>

      {/* Empty Date Navigation Bar */}
      <div className="date-navigation">
        <div className="flex flex-col sm:flex-row justify-center items-center w-full px-4">
          {/* Empty space - no buttons */}
        </div>
      </div>

      {/* Upcoming Tips Teaser - For logged out users - Below gray bar */}
      {!teaserLoading && !teaserError &&
       teaserData?.upcomingRacesTeaser &&
       teaserData.upcomingRacesTeaser.length > 0 && (
        <section className="mb-4 px-4 mt-4">
          <UpcomingTips races={teaserData.upcomingRacesTeaser} isAuthenticated={false} showRegisterButton={false} />
        </section>
      )}
      
      {/* Show error information for debugging - can be removed in production */}
      {teaserError && (
        <div className="mb-4 px-4 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-red-800">Upcoming Tips Temporarily Unavailable</h3>
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-2">
                <summary className="text-xs text-red-600 cursor-pointer">Technical Details (Dev Only)</summary>
                <pre className="text-xs text-red-600 mt-1 overflow-auto">
                  {JSON.stringify(teaserError, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Top Register Button - Desktop */}
        <div className="hidden md:block text-center mb-8">
          <button
            onClick={handleGetStarted}
            className="bg-racing-green hover:bg-racing-green-dark text-white font-bold py-4 px-8 rounded-lg text-xl transition-colors duration-200 shadow-lg transform hover:scale-105"
          >
            🚀 Access Your Tips Now
          </button>
        </div>
        
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-deep-navy to-racing-green text-white rounded-lg shadow-card mb-8 overflow-hidden">
          <div className="p-8 text-center">
            {promotion.imageUrl && (
              <div className="mb-6">
                <img
                  src={promotion.imageUrl}
                  alt={promotion.title}
                  className="mx-auto rounded-lg shadow-lg border-2 border-white/20 object-cover"
                  style={{ aspectRatio: '1/1', maxWidth: '200px', maxHeight: '200px' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-race-gold">
              {promotion.title}
            </h1>
            
            <div className="text-lg md:text-xl mb-6 opacity-90">
              {promotion.description ? (
                <div
                  dangerouslySetInnerHTML={{ __html: promotion.description }}
                  className="prose prose-invert max-w-none"
                />
              ) : (
                <p>Get exclusive access to data-driven racing insights and intelligent predictions</p>
              )}
            </div>
          </div>
        </div>

        {/* Today's Racing Preview - Meeting Tiles */}
        {!meetingsLoading && !meetingsError && hasAnyMeetings && (
          <div className="px-4 py-6 bg-gray-50 border-t-4 border-racing-green rounded-lg mb-8">
            <h4 className="text-2l font-heading font-bold text-deep-navy mb-4 text-center">
              Today's Racing Preview
            </h4>
            
            <div className="max-w-4xl mx-auto">
              {/* Just Display Metropolitan Meetings On Promo Page */}
              {groupedMeetings.metropolitan.length > 0 && (
                <div className="mb-6 text-center">
                  {/* <h3 className="text-lg font-heading font-bold text-deep-navy mb-3">
                    Metropolitan Tracks
                  </h3> */}
                  <div className="flex flex-wrap justify-center gap-4">
                    {groupedMeetings.metropolitan.map(meeting => (
                      <MeetingTile key={meeting.meeting_id} meeting={meeting} />
                    ))}
                  </div>
                </div>
              )}

            </div>
            
            {/* Total Winners Definition */}
            <div className="text-center mt-4 mb-2">
              <p className="text-xs text-gray-500">
                *Total Winners includes all winning selections across Top 1, 2, 3, and Value picks
              </p>
            </div>
          </div>
        )}

        {/* Offer Card */}
        <div className="bg-white rounded-lg border-2 border-racing-green shadow-lg p-8 mb-8">
          <div className="text-center">
            <div className="bg-racing-green text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💰</span>
            </div>
            
            <h3 className="text-2xl font-bold text-deep-navy mb-4">
              Race Day Pass
            </h3>
            
            <p className="text-gray-600 mb-6">
              Join now to access all the data-driven racing intelligence
            </p>

            {/* Discount Code Display */}
            {/*<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 max-w-md mx-auto">*/}
            {/*  <div className="flex items-center justify-center mb-3">*/}
            {/*    <span className="text-yellow-600 mr-2">🏆</span>*/}
            {/*    <span className="font-semibold text-yellow-800">Exclusive Offer</span>*/}
            {/*  </div>*/}
            {/*  <p className="text-sm text-yellow-700 mb-3">*/}
            {/*    Click to copy your special discount code:*/}
            {/*  </p>*/}
            {/*  <button*/}
            {/*    onClick={handleCopyCode}*/}
            {/*    className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-yellow-900 font-mono font-bold text-lg py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 border border-yellow-300"*/}
            {/*  >*/}
            {/*    <span>{promotion.discountCode}</span>*/}
            {/*    {copied ? (*/}
            {/*      <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">*/}
            {/*        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />*/}
            {/*      </svg>*/}
            {/*    ) : (*/}
            {/*      <svg className="h-5 w-5 text-yellow-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
            {/*        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />*/}
            {/*      </svg>*/}
            {/*    )}*/}
            {/*  </button>*/}
            {/*  {copied && (*/}
            {/*    <p className="text-xs text-green-600 mt-2 font-medium">*/}
            {/*      ✓ Copied to clipboard!*/}
            {/*    </p>*/}
            {/*  )}*/}
            {/*</div>*/}

            <div className="space-y-4 mb-8 text-left max-w-md mx-auto">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-racing-green mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700">Daily data-driven racing insights</span>
              </div>
              <div className="flex items-start">
                <svg className="h-5 w-5 text-racing-green mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700">Intelligent predictions & statistical analysis</span>
              </div>
              {/* <div className="flex items-start">
                <svg className="h-5 w-5 text-racing-green mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700">Access to every Metro tip for just $5 today with promo code 5METRO<br/></span>
              </div> */}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetStarted}
                className="px-8 py-3 bg-racing-green text-white rounded-lg font-medium hover:bg-racing-green-dark transition-colors"
              >
                🚀 Get Started Now
              </button>
              
              <button
                onClick={handleSignIn}
                className="px-8 py-3 border-2 border-deep-navy text-deep-navy rounded-lg font-medium hover:bg-deep-navy hover:text-white transition-colors"
              >
                Already a Member? Sign In
              </button>
            </div>
          </div>
        </div>

        {/* Campaign Attribution Info */}
        <div className="text-center text-sm text-gray-500">
          <p>
            Campaign: {promotion.source}
            {promotion.discountCode && <> | Discount Code: {promotion.discountCode}</>}
          </p>
        </div>
        </div>
      </main>

      {/* Mobile Sticky Register Button - always visible at bottom */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="p-4 pb-safe">
          <button
            onClick={handleGetStarted}
            className="w-full bg-racing-green hover:bg-racing-green-dark text-white font-bold py-4 px-6 rounded-lg text-lg transition-colors duration-200 shadow-lg"
          >
            🚀 Register For Your Tips Now
          </button>
        </div>
      </div>

      {/* Mobile spacing to prevent content being hidden behind sticky button */}
      <div className="md:hidden h-20"></div>
    </div>
  );
};

export default PromoPage;